'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, type ReactNode, useCallback, useContext, useMemo } from 'react';
import { usePack } from '@/app/(platform)/(subscription-gated)/labs/_engine/pack-context';
import type { LabsPreviewEventName } from '@/lib/analytics/labs-preview-events';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import type { PreviewPlacement } from './entry-placement';

/**
 * Analytics for the public preview. The deliberate NON-reuse of `useLabsAnalytics`.
 *
 * 🔴 WHY NOT REUSE THE SPRINT'S HOOK. `useLabsAnalytics` no-ops until the canonical Labs ARM flag
 * resolves, and stamps that arm on every event. Both are correct for the sprint and wrong here:
 *
 *   1. An anonymous reader has no arm to resolve, so reusing it would mean NOT ONE EVENT EVER FIRES,
 *      and it would fail SILENTLY. That is the worst way for an experiment to fail, and it is the
 *      single most likely way to build this surface and get zero data on launch day.
 *   2. Stamping `arm` would make preview events look enrollable in Experiment 3.
 *
 * So this hook never reads a flag and never has a readiness gate: the first event can fire on mount.
 *
 * Every event carries the same five properties, which is what makes the funnel readable per page:
 *   - `vertical`: which lab (finance, data-analytics, consulting)
 *   - `guide_source`: the `?src=` slug of the guide page that linked here, or 'direct'
 *   - `preview_visitor_id`: the anonymous browser id, so completion is per VISITOR, not per pageview
 *   - `entry`: 'question' if the reader answered the in-page card, 'link' if they clicked a plain CTA
 *   - `placement`: 'article' for the in-body block, 'newsletter' for the subscribe-confirmation link
 *
 * 🔴 `entry` IS WHAT MAKES THE IN-PAGE QUESTION FALSIFIABLE. Both placements land on the same route
 * with the same `?src=`, so without it the snippet could lift completions on a page and we would have
 * no way to attribute the lift to it. With it, the same funnel splits by entry path, on the same page,
 * over the same weeks, which is a cleaner comparison than any arm we could assign.
 *
 * 🔴 `placement` IS THE SAME ARGUMENT ONE LEVEL UP, and it is not redundant with `entry`. The
 * newsletter-confirmation link is a plain link, so it reports `entry: 'link'` exactly like the article
 * block's fallback CTA does. Without `placement` those two pool, and since one slot competes with the
 * newsletter form and the other cannot, pooling them averages two opposite effects into nothing. See
 * `entry-placement.ts` for the 90 / 39 / 1 capture read that makes the distinction load-bearing.
 */

interface PreviewAnalyticsValue {
  entry: string;
  guideSource: string;
  placement: PreviewPlacement;
  track: (event: LabsPreviewEventName, props?: Record<string, unknown>) => void;
  visitorId: string | null;
}
const PreviewAnalyticsContext = createContext<PreviewAnalyticsValue | null>(null);
export function PreviewAnalyticsProvider(t0) {
  const $ = _c(16);
  const {
    children,
    entry,
    guideSource,
    placement,
    visitorId
  } = t0;
  const {
    track: platformTrack
  } = useAnalytics();
  const {
    slug
  } = usePack();
  let t1;
  if ($[0] !== entry || $[1] !== guideSource || $[2] !== placement || $[3] !== platformTrack || $[4] !== slug || $[5] !== visitorId) {
    t1 = (event, props) => {
      platformTrack(event, {
        entry,
        guide_source: guideSource,
        placement,
        preview_visitor_id: visitorId,
        vertical: slug,
        ...props
      });
    };
    $[0] = entry;
    $[1] = guideSource;
    $[2] = placement;
    $[3] = platformTrack;
    $[4] = slug;
    $[5] = visitorId;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const track = t1;
  let t2;
  if ($[7] !== entry || $[8] !== guideSource || $[9] !== placement || $[10] !== track || $[11] !== visitorId) {
    t2 = {
      entry,
      guideSource,
      placement,
      track,
      visitorId
    };
    $[7] = entry;
    $[8] = guideSource;
    $[9] = placement;
    $[10] = track;
    $[11] = visitorId;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const value = t2;
  let t3;
  if ($[13] !== children || $[14] !== value) {
    t3 = <PreviewAnalyticsContext.Provider value={value}>{children}</PreviewAnalyticsContext.Provider>;
    $[13] = children;
    $[14] = value;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}

/**
 * Preview analytics. Throws with no provider, unlike the surface context, there is no safe default
 * here: a preview screen that silently fails to track is exactly the failure this file exists to
 * prevent, so it must be impossible to mount one outside the provider.
 */
export function usePreviewAnalytics() {
  const value = useContext(PreviewAnalyticsContext);
  if (value === null) {
    throw new Error("usePreviewAnalytics() must be called inside a <PreviewAnalyticsProvider>.");
  }
  return value;
}